import { replace } from 'connected-react-router'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'

import CreateModalFooterWithItinerary from './CreateModalFooterWithItinerary'
import { CreateItemProps } from './index'
import { CreateAirBnbItemViewModel, CreateHotelItemViewModel } from './models'
import {
  createHotelPayloadFromViewModel,
  createAirbnbPayloadFromViewModel,
} from './utils'
import AirBnbFields from '../Common/AirBnbFields'
import { formOptions } from '../Common/Fields/config'
import HotelFields from '../Common/HotelFields'

import {
  addToTripSaveErrorEvent,
  addToTripSaveEvent,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LineHomeIcon from 'components/Luxkit/Icons/line/LineHomeIcon'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useCreateTripItem } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { TripItem } from 'tripPlanner/types/tripItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

type ViewModel = CreateAirBnbItemViewModel | CreateHotelItemViewModel

const ChipContainer = styled(Group)`
  margin-bottom: ${rem(16)};
  margin-top: ${rem(20)};
`

const SELECT_OPTIONS: Array<{
  value: ViewModel['type']
  label: string
  icon: React.ReactElement
}> = [
  { value: 'HOTEL', label: 'Hotel', icon: <LineBedIcon /> },
  { value: 'AIRBNB', label: 'Home or Villa', icon: <LineHomeIcon /> },
]

function CreateAccommodation(props: CreateItemProps) {
  const { tripId, defaultDate } = props

  const { closeModal, goBack } = useModalCallbacks()

  const methods = useForm<ViewModel>({
    ...formOptions,
    defaultValues: {
      type: 'HOTEL',
      sourceType: 'NA',
      isBooked: false,
      startDate: defaultDate,
      endDate: defaultDate
        ? moment(defaultDate).add(1, 'day').clone()?.format(ISO_DATE_FORMAT)
        : undefined,
      place: {},
      isInItinerary: !!defaultDate,
    },
  })
  const { handleSubmit, watch, setValue } = methods
  const dispatch = useAppDispatch()

  const type = watch('type')

  const onInvalid = useCallback(() => {
    fireInteractionEvent(addToTripSaveErrorEvent('accommodation_custom'))
  }, [])

  const { mutate: createMutate, isLoading } = useCreateTripItem({
    onSuccess: ({ id }: TripItem) => {
      fireInteractionEvent(addToTripSaveEvent('accommodation_custom'))
      dispatch(replace({ hash: itemSectionId(id) }))
      closeModal()
    },
    onError: onInvalid,
  })

  const createAccommodationItem = useCallback(
    (viewModel: CreateAirBnbItemViewModel | CreateHotelItemViewModel) => {
      switch (viewModel.type) {
        case 'AIRBNB': {
          createMutate({
            tripId,
            tripItem: createAirbnbPayloadFromViewModel(viewModel),
          })
          break
        }

        case 'HOTEL': {
          createMutate({
            tripId,
            tripItem: createHotelPayloadFromViewModel(viewModel),
          })
          break
        }
      }
    },
    [createMutate, tripId],
  )

  return (
    <>
      <ModalHeader
        title="Add your accommodation"
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <FormProvider {...methods}>
        <ModalBody>
          <ModalContent>
            <ChipContainer
              direction="horizontal"
              verticalAlign="center"
              gap={8}
              wrap="wrap"
            >
              {SELECT_OPTIONS.map((option) => (
                <FilterChip
                  key={option.value}
                  startIcon={option.icon}
                  onClick={() => setValue('type', option.value)}
                  selected={type === option.value}
                  size="medium"
                >
                  {option.label}
                </FilterChip>
              ))}
            </ChipContainer>

            {type === 'HOTEL' && <HotelFields tripId={tripId} />}
            {type === 'AIRBNB' && <AirBnbFields tripId={tripId} />}
          </ModalContent>
        </ModalBody>

        <CreateModalFooterWithItinerary
          onSubmit={handleSubmit(createAccommodationItem, onInvalid)}
          isLoading={isLoading}
          tripId={tripId}
        />
      </FormProvider>

      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default CreateAccommodation
